import { useEffect, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Table, Tooltip, Typography, message } from 'antd';
import { useSearchParams } from 'react-router-dom';

import {
	useGetAllCreators,
	useGetCreatorsByCategoryId,
} from '@/api/monitor_collections';
import { useDebounce } from '@/hooks';
import { prepareTableColumnsWithQueryParams } from '@/lib/helpers/prepareTableColumnsWithQueryParams';
import { IPagination, TCreatorsSortBy, TSortOrder } from '@/ts';

import styles from './styles.module.css';

import { columns } from './data';

function Creators({ id }: { id: string }) {
	const [searchParams, setSearchParams] = useSearchParams({});
	const [search, setSearch] = useState('');

	const [pagination, setPagination] = useState<IPagination>({
		page: Number(searchParams.get('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(searchParams.get('limit') ?? 10),
	});
	const [sortBy, setSortBy] = useState<TCreatorsSortBy>(
		(searchParams.get('sortBy') as TCreatorsSortBy) ?? 'profileName',
	);
	const [sortOrder, setSortOrder] = useState<TSortOrder>(
		(searchParams.get('sortOrder') as TSortOrder) ?? 'asc',
	);
	const [filters, setFilters] = useState<string[]>(
		searchParams.get('filters')?.split(',') ?? [],
	);
	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isFetching, refetch } = useGetCreatorsByCategoryId(
		id!,
		{
			pagination,
			sortBy,
			sortOrder,
			filters: filters.length ? filters.join(',') : '',
			search: debouncedSearch.length >= 3 ? debouncedSearch : '',
		},
	);

	const creatorsItems = data?.data?.data?.items;
	const total = data?.data?.data?.pagination?.total ?? 0;

	useEffect(() => {
		if (search.length) {
			setPagination((cur) => ({ ...cur, page: 1 }));
		}
	}, [search]);

	useEffect(() => {
		const sortByParam = searchParams.get('sortBy');
		const sortOrderParam = searchParams.get('sortOrder');
		const pageParam = searchParams.get('page');
		const limitParam = searchParams.get('limit');
		const filtersParam = searchParams.get('filters');

		setSearchParams({
			sortBy: sortByParam ?? 'profileName',
			sortOrder: sortOrderParam ?? 'asc',
			page: pageParam ?? pagination?.page?.toString(),
			limit: limitParam ?? pagination?.limit?.toString(),
			filters: filtersParam ?? '',
		});
	}, []);

	const onTableChange = (__: any, filters: any, sorter: any) => {
		const { field = 'profileName', order = 'ascend' } = sorter;
		const platform = filters.platform;

		if (field) {
			setSortBy(field);
			setSearchParams((searchParams) => {
				searchParams.set('sortBy', field);
				return searchParams;
			});
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
			setSearchParams((searchParams) => {
				searchParams.set('sortOrder', order === 'ascend' ? 'asc' : 'desc');
				return searchParams;
			});
		}

		if (platform) {
			setFilters(platform as string[]);
			setSearchParams((searchParams) => {
				searchParams.set('filters', platform.join(','));
				return searchParams;
			});
		} else {
			setSearchParams((searchParams) => {
				searchParams.set('filters', '');
				return searchParams;
			});
			setFilters([]);
		}
	};

	return (
		<Flex vertical style={{ width: '100%' }} gap={12}>
			<Typography.Title level={5} style={{ margin: 0 }}>
				Creators
			</Typography.Title>

			{/* <div className={styles.inputBtnWrapper}>
				<Input
					style={{ width: '50%' }}
					placeholder={'Type to search'}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div> */}

			<Table
				loading={isLoading || isFetching}
				style={{ width: '100%' }}
				size="small"
				scroll={{ x: 700 }}
				onChange={onTableChange}
				dataSource={
					creatorsItems?.map((creator) => {
						creator.category_id = id;
						return creator;
					}) ?? []
				}
				columns={[...prepareTableColumnsWithQueryParams(columns, searchParams)]}
				rowKey={'_id'}
				bordered
				pagination={{
					total: total,
					current: pagination.page,
					showSizeChanger: true,
					pageSizeOptions: [10, 25, 50],
					onChange: (page: number, pageSize: number) => {
						setPagination((cur) => ({ ...cur, page, limit: pageSize }));
						setSearchParams((searchParams) => {
							searchParams.set('page', page.toString());
							searchParams.set('limit', pageSize.toString());
							return searchParams;
						});
					},
				}}
			/>
		</Flex>
	);
}

export default Creators;
